/**
 * Asymmetric two-slot selection bus — Zustand store for the cross-pane
 * "what is the operator focused on right now" value.
 *
 * Stream A PR 5.1 hoisted from the M5 minimal React-Context seam to a
 * kind-discriminated Zustand store with a *single* ``selected`` value
 * reflected everywhere (map ring, list highlight + scroll, Entity
 * Details). The selection-sync redesign (Phase 1, 2026-06-09) splits
 * that single slot into **two** so the operator can inspect one entity
 * in the lists / Entity Details while the map ring stays put on another.
 *
 * # The two slots
 *
 * - ``inspected`` — what the **Entity Details** panel renders, and which
 *   row the Platforms / Detections / Alerts lists highlight + scroll into
 *   view. This is the operator's reading focus.
 * - ``mapSelected`` — what the **map** rings + pans its camera to, and
 *   whose platform **trail** (REQ-UI-012) renders. This is the operator's
 *   geographic focus.
 *
 * Each slot independently holds a :type:`SelectionValue` or ``null``.
 *
 * # Why two slots — the intended divergence
 *
 * The single-slot bus coupled "click a list row" to "move the map ring".
 * Operators triaging a long Detections/Alerts/Platforms list want to skim
 * row after row (reading each one's detail) *without* yanking the map
 * camera off the area they're watching. So the redesign makes list
 * left-click set ``inspected`` only — the map ring stays where it was.
 * After a list left-click the two slots legitimately diverge: the lists +
 * Entity Details point at row B, while the map ring + trail stay on the
 * previously map-selected A. Pushing a list selection *to* the map is an
 * explicit, separate "Select on Map" affordance (Phase 2 — not in this
 * store yet).
 *
 * Map-glyph / marker clicks remain symmetric: clicking a map entity sets
 * **both** slots (you're looking at it on the map *and* you want its
 * detail), so the map-originated path behaves exactly as before.
 *
 * # Action → slot routing
 *
 * | Action            | ``inspected`` | ``mapSelected`` | Originators                          |
 * |-------------------|---------------|-----------------|--------------------------------------|
 * | :func:`inspect`   | set           | untouched       | list-row left-click; cross-pane links |
 * | :func:`selectOnMap`| set          | set             | map-glyph/marker click; "Show on map" |
 * | :func:`clear`     | → null        | → null          | map background-click; Escape          |
 * | :func:`toggleInspect` | set, or → null | untouched, or → null | list-row left-click (2026-10-01): a different entity inspects; the already-inspected entity deselects — both slots |
 *
 * Two locked product defaults (architect-decided, 2026-06-09):
 *   1. The platform **trail** follows ``mapSelected`` (the trail is a map
 *      affordance, not a reading affordance).
 *   2. **Deselect** (background click / Escape) clears **both** slots — a
 *      single "nothing is focused" gesture resets the whole bus.
 *
 * # Selection value shape
 *
 * ``SelectionValue = { kind: "platform" | "aggregator" | "detection" |
 * "alert"; id: string }``. ``null`` (in either slot) means no selection
 * for that slot. The ``kind`` discriminator drives which Entity Details
 * section schema renders (per ADR-024 §4) and which panels can highlight
 * (per ADR-024 §5 — e.g., the map only rings ``platform`` selections from
 * the snapshot layer because aggregators are map-invisible per the PR 5
 * §6 capture).
 *
 * # Single-focus rule (within a slot)
 *
 * Per ADR-024 §5 + Flow Q §"Synchronized-selection contract": within each
 * slot only one entity is focused at a time. Setting a different entity
 * replaces the previous one. Concurrent writes are last-writer-wins per
 * Zustand semantics. The *cross-slot* divergence above is the deliberate
 * exception the redesign introduces — it is two single-focus slots, not
 * one multi-select slot.
 *
 * # Fast-click deduplication
 *
 * Flow Q §"Edge cases" calls for trailing-click deduplication within a
 * 100 ms window. Zustand's last-writer-wins semantics already collapse
 * multiple writes within the same microtask; subscribers see only the
 * final value. The 100 ms window matters only for downstream side-effects
 * (e.g., TanStack-Query-driven trail fetches) and is handled at the
 * consumer (TanStack Query's query-cancellation via ``signal``), not here.
 *
 * # Testing
 *
 * Module-level state requires tests to reset between cases. The pattern
 * is::
 *
 *     beforeEach(() => {
 *       useSelectionStore.setState({ inspected: null, mapSelected: null });
 *     });
 *
 * Production code never calls ``setState`` directly — always via the
 * ``inspect`` / ``selectOnMap`` / ``clear`` actions.
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state).
 *   - REQ-UI-051 (synchronized-selection bus contract).
 *   - REQ-UI-063 (cross-pane navigation / "Show on map").
 *   - ADR-024 §5 (selection bus).
 *   - docs/engineering/detail/storyboards/flows/Q-side-by-side-map-platforms/README.md.
 */
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";

/**
 * The entity kinds a selection can refer to.
 *
 * - ``platform`` — a Producer / sensor. Map-clickable, Platforms-
 *   list-clickable. Drives the Platform section schema in Entity
 *   Details (ADR-024 §4 — STATUS / POSITION / CONNECTIVITY /
 *   RECENT MESSAGES / CONFIGURATION / RECENT ERRORS).
 * - ``aggregator`` — an integrating Aggregator multiplexing N
 *   Producers. Map-invisible per the 2026-05-27 PR 5 §6 decision
 *   (no map marker — aggregators are network entities, not
 *   geographic). Platforms-list-header-clickable. Drives the
 *   Aggregator section schema (STATUS / CONNECTIVITY / MESSAGE
 *   RATES / MULTIPLEXED PLATFORMS / CONFIGURATION / RECENT
 *   ERRORS).
 * - ``detection`` — a SAPIENT DetectionReport object (ADR-032; the
 *   successor to the retired data-plane Event kind, removed in PR6).
 *   Map-glyph-clickable + Detections-list-clickable (PR5a inc 2).
 *   Drives the Detection section schema in Entity Details (PR5a inc 4).
 * - ``alert`` — a SAPIENT Alert (ADR-029 / ADR-032). Alerts-list- and
 *   notification-clickable (PR5a inc 3). Drives the Alert section
 *   schema in Entity Details (PR5a inc 4).
 * - ``query-record`` — one row of a Query-panel result set (ADR-038
 *   §Decision 5 / REQ-UI-123). Unlike the four above it is not a
 *   *domain entity* but a record of an arbitrary queryable type, so it
 *   carries the type id alongside the record id — the pair is what the
 *   BFF's detail route is keyed by. Query-panel-clickable only: it
 *   reaches the ``inspected`` slot and **never** ``mapSelected``, so a
 *   query selection cannot move the map camera (ADR-024 §5 asymmetric
 *   bus). It has no list panel to reveal and no map glyph.
 * - ``status-component`` / ``status-peer`` / ``status-interface`` /
 *   ``status-connection`` — the four levels of the System Status
 *   hierarchy (ADR-024 §4/§5 amended 2026-07-24, again 2026-07-31 for the
 *   third entity type, and again 2026-08-05 for the fourth;
 *   Flow L OQ-1 resolution). Like ``query-record`` they are not domain
 *   entities and never reach ``mapSelected`` (no glyph, no list row to
 *   reveal); their detail renders in Entity Details off the **live**
 *   System Status feed, not a per-id BFF route. A ``status-connection``
 *   carries a composite identity — ``(component_id, connection_id)`` —
 *   like ``query-record``'s ``(recordType, id)``. Originated **only** by
 *   the System Status panel and the status Selection Detail
 *   (:func:`inspectStatus`), which every authenticated role can open
 *   (REQ-STATUS-029).
 *
 * # The System Status hierarchy is three levels, not three synonyms
 *
 * Architect ruling 2026-07-31 (feature-spec §2.2; REQ-STATUS-039):
 *
 * | level       | kind                 | drawn on the canvas as        |
 * |-------------|----------------------|-------------------------------|
 * | component   | ``status-component`` | a node                        |
 * | peer        | ``status-peer``      | one icon in the Peer Manager's stack |
 * | interface   | ``status-interface`` | one icon in the Ingress stack |
 * | connection  | ``status-connection``| **nothing** — a roster row    |
 *
 * A component hosts N peers; a **peer** hosts N interfaces; an
 * **interface** hosts N connections (a CDS peer interface with four TCP
 * connections is four roster rows, not four icons). Connections are never
 * drawn on the canvas at any count — at the 1,000+ ceiling one icon per
 * connection is a category error. The ``status-connection`` kind is
 * retained as what defines the field set an expanded roster **drawer**
 * renders; expanding a drawer is a disclosure inside the host's Selection
 * Detail and does **not** move the bus, so ADR-024 §5's one-focus contract
 * is unaffected.
 *
 * # Why the peer is its own kind rather than a reused ``status-interface``
 *
 * ADR-039 §D-1 put a **peer** between the component and the interface, and
 * the Peer Manager's stack enumerates peers rather than interfaces
 * (REQ-STATUS-081). Reusing ``status-interface`` with a peer's name in
 * ``interfaceName`` was the smaller diff and is the wrong one: the field
 * is documented as the config ⋈ status join key, and a peer does not join
 * the way an interface does — it reports under a namespaced
 * ``peer:<name>`` ``component_id``, because a peer and one of its own
 * interfaces may share a name. A reused kind would have carried a name
 * that resolves to the wrong entity, and every selector built on it would
 * have inherited that.
 */
export type SelectionKind =
  | "platform"
  | "aggregator"
  | "detection"
  | "alert"
  | "track"
  | "query-record"
  | "status-component"
  | "status-peer"
  | "status-interface"
  | "status-connection";

/**
 * A selected entity or record.
 *
 * A discriminated union rather than a flat ``{kind, id}`` because the
 * ``query-record``, ``status-interface`` and ``status-connection`` kinds
 * are not addressed by a single ``id``: a record id is unique only within
 * its queryable type, a connection id only within its owning component,
 * and an interface is named only within its parent node. Making those
 * union members rather than optional fields keeps the extra coordinate
 * from looking meaningful on the entity kinds, and makes the compiler
 * enforce that a caller constructing one supplies it.
 */
export type SelectionValue =
  | EntitySelection
  | QueryRecordSelection
  | StatusComponentSelection
  | StatusPeerSelection
  | StatusInterfaceSelection
  | StatusConnectionSelection;

/**
 * The System Status kinds — a topology node, one of its peers, an interface, or a
 * drilled-in connection (ADR-024 §4/§5 amended 2026-07-24, 2026-07-31 and
 * 2026-08-05). Grouped so :func:`inspectStatus` (the single-focus originator)
 * takes exactly these.
 */
export type StatusSelection =
  | StatusComponentSelection
  | StatusPeerSelection
  | StatusInterfaceSelection
  | StatusConnectionSelection;

/**
 * A selected domain entity. ``id`` is the kind-appropriate identifier
 * (Producer UUID for platforms, Aggregator UUID for aggregators,
 * DetectionReport ID for detections, Alert ID for alerts, the BFF-derived
 * ``track_id`` for object tracks — ADR-041 D-5 / REQ-UI-171).
 */
export interface EntitySelection {
  readonly kind: "platform" | "aggregator" | "detection" | "alert" | "track";
  readonly id: string;
}

/**
 * A selected Query-panel result row (REQ-UI-123).
 *
 * ``recordType`` is the queryable type id from the catalog
 * (``sapient_detection``, ``raw_blob``, …); ``id`` is that type's natural
 * record id. Both are needed to address
 * ``GET /api/query/records/{type}/{id}``.
 */
export interface QueryRecordSelection {
  readonly kind: "query-record";
  readonly id: string;
  readonly recordType: string;
}

/**
 * A selected System Status topology node (ADR-024 §4 amended 2026-07-24;
 * REQ-STATUS-027). ``id`` is the topology **node id** (e.g. ``message-bus``,
 * ``sapient-ingress-conns``); Entity Details resolves the node's kind,
 * components, and stoplight from the shared live feed (:func:`useResolvedSystemStatus`),
 * not a per-id BFF route (the Query flow relies on that route's absence).
 */
export interface StatusComponentSelection {
  readonly kind: "status-component";
  readonly id: string;
}

/**
 * A selected System Status **interface** — one icon in an ``Ingress`` /
 * ``Egress`` node's stack (ADR-024 §4 amended 2026-07-31; REQ-STATUS-039).
 *
 * Addressed by ``(parentNodeId, interfaceName)`` and **not** by an ``id``, for
 * two reasons that both matter:
 *
 *   1. An interface is *not* a topology node. Entity Details resolves a
 *      ``status-component`` id against the static ``TOPOLOGY_NODES`` array and
 *      renders an empty state on a miss (the drift branch), so reusing that
 *      kind for an interface would render a silently empty panel.
 *   2. ``interfaceName`` is the operator's own label and the join key between
 *      the config roster (``GET /api/admin/peers/interfaces``, authoritative
 *      for *existence*) and the status feed (authoritative for *health*) —
 *      a direct peer interface reports with ``component_id == config.name``.
 *      Naming the field for what it is keeps that join visible at every call
 *      site rather than hiding it behind a generic ``id``.
 *
 * ``parentNodeId`` is the hosting node — carried so the parent stays visually
 * highlighted while a child interface is inspected (feature-spec §2.2), and so
 * two interfaces that happen to share a name under different parents stay
 * distinct selections.
 */
export interface StatusInterfaceSelection {
  readonly kind: "status-interface";
  /** The operator's interface label — the config ⋈ status join key. */
  readonly interfaceName: string;
  /** The ``Ingress`` / ``Peer Manager`` topology node hosting this interface. */
  readonly parentNodeId: string;
  /**
   * The owning peer's name when this interface was reached **through** a peer,
   * or ``undefined`` for an ``Ingress`` interface, which has none.
   *
   * Carried so the pane can name the full ``peer / interface`` path and step
   * back to the peer rather than skipping to the node — the peer is the only
   * place this interface's siblings are listed together (REQ-STATUS-086), and
   * jumping past it would lose the level the operator navigated through. It is
   * *not* part of the identity: an interface name is unique deployment-wide
   * (REQ-PEER-071), so adding the peer to the key would let one entity hold two
   * distinct selections.
   */
  readonly peerName?: string;
}

/**
 * A selected System Status **peer** — one icon in the Peer Manager's stack
 * (ADR-024 §4 amended 2026-08-05; ADR-039 §D-1; REQ-STATUS-081 / -086).
 *
 * Addressed by ``(parentNodeId, peerName)``, mirroring
 * :type:`StatusInterfaceSelection` for the same first reason: a peer is not a
 * topology node, so reusing ``status-component`` would resolve against
 * ``TOPOLOGY_NODES``, miss, and render the drift branch's empty panel.
 *
 * ⚠ ``peerName`` is the operator's label, **not** the ``component_id``. The feed
 * reports a peer under ``peer:<name>`` (:func:`peerComponentId`), and the
 * namespace is applied at the join rather than stored here, so the selection
 * carries what the operator sees and what the peer-configuration routes are
 * keyed by. Storing the namespaced form would put a wire detail on the bus and
 * make every consumer strip it back off.
 */
export interface StatusPeerSelection {
  readonly kind: "status-peer";
  /** The operator's peer label; ``peer:<name>`` is its component id. */
  readonly peerName: string;
  /** The ``Peer Manager`` topology node hosting this peer. */
  readonly parentNodeId: string;
}

/**
 * A selected System Status connection (ADR-024 §5 amended 2026-07-24;
 * REQ-STATUS-027, Flow L §5). Composite identity per ADR-024 §5 — ``id`` is the
 * ``connection_id``, unique only within ``componentId`` (the owning connector's
 * BFF ``component_id``), so both address it. ``nodeId`` is the topology node the
 * connection was drilled from — carried so Entity Details can step "back" to that
 * node (Flow L §9) without re-deriving which of a connector's two nodes (service
 * vs. connections satellite) originated the drill.
 */
export interface StatusConnectionSelection {
  readonly kind: "status-connection";
  readonly id: string;
  readonly componentId: string;
  readonly nodeId: string;
}

/**
 * How many link steps "back" can walk (REQ-UI-186).
 *
 * Bounded because the history lives for the session and a chain of
 * cross-pane links has no natural end — a track's members link to
 * detections, which link to platforms and to each other. Twenty is far
 * past any real traversal while keeping the list a fixed cost. Older
 * entries are dropped from the front, so the *recent* steps — the ones an
 * operator is actually retracing — are the ones that survive.
 */
export const NAVIGATION_HISTORY_LIMIT = 20;

/**
 * The selection store's shape — two independent slots plus the actions
 * that route writes between them.
 *
 * Actions are exposed alongside state so the same hook can both read and
 * write — Zustand idiom. The action references are stable across renders
 * (per Zustand's documented contract), so they're safe to include in
 * React effect dependency arrays.
 *
 * Cross-references:
 *   - REQ-UI-051 / REQ-UI-063; ADR-024 §5.
 */
export interface SelectionState {
  /**
   * The entity the Entity Details panel renders + the lists
   * highlight/scroll to. The operator's reading focus. Set by
   * :func:`inspect` (list-row left-click, cross-pane links) and by
   * :func:`selectOnMap` (map-glyph click sets both slots).
   */
  readonly inspected: SelectionValue | null;
  /**
   * The entity the map rings + pans to, and whose platform trail
   * renders. The operator's geographic focus. Set only by
   * :func:`selectOnMap` (map-glyph click, "Show on map"); a plain
   * list-row left-click leaves it untouched (the intended divergence).
   */
  readonly mapSelected: SelectionValue | null;
  /**
   * The selections a chain of **link navigations** came through, oldest
   * first, so Selection Details can step back to the one that led here
   * (REQ-UI-186).
   *
   * # Why only link navigations are recorded
   *
   * A link inside Selection Details — a track's member detection, a
   * detection's DETECTED BY platform, an alert's associated detection — is
   * a *traversal*: the operator went somewhere **from** somewhere, and
   * losing the way back used to mean re-finding the origin in a list, or
   * re-running the query that produced it. So :func:`inspectLinked` pushes
   * the selection it is leaving.
   *
   * Every other originator — a list row, a map glyph, a Query result row,
   * a status node, a deselect — starts a **new root** and clears this.
   * That is the whole design: if every change pushed, skimming a 200-row
   * Detections list would build 200 entries and "back" would replay the
   * skim rather than returning to the track the operator was reading.
   *
   * Bounded at :data:`NAVIGATION_HISTORY_LIMIT`.
   */
  readonly navigationHistory: readonly SelectionValue[];
  /**
   * Set ``inspected`` ONLY, leaving ``mapSelected`` untouched.
   *
   * The list-row left-click + cross-pane-link path. After this the two
   * slots may diverge (lists/Entity Details on the new value; map ring
   * still on the prior ``mapSelected``) — that is the redesign's intent,
   * not a bug.
   */
  readonly inspect: (value: SelectionValue) => void;
  /**
   * The list-row left-click path since 2026-10-01 (operator request;
   * REQ-UI-051 amended): behaves as :func:`inspect` unless ``value`` is the
   * entity already in ``inspected``, in which case the click **deselects** —
   * both slots clear, exactly as :func:`clear` does, so the row highlight,
   * Entity Details and the map ring leave together and the camera stays put.
   * Shared by every list panel so the gesture is identical across them; the
   * cross-pane links keep using :func:`inspect` (a link always inspects).
   */
  readonly toggleInspect: (value: EntitySelection) => void;
  /**
   * Set BOTH ``inspected`` and ``mapSelected`` to the same value.
   *
   * The map-glyph/marker-click + "Show on map" path: the operator is
   * looking at the entity on the map AND wants its detail, so both the
   * reading focus and the geographic focus move together.
   *
   * Takes an :type:`EntitySelection` only. A Query result row has no map
   * glyph and must never move the camera (ADR-038 §Decision 5 / ADR-024
   * §5), so the type system — not a runtime check — is what keeps the
   * ``query-record`` kind out of the map slot.
   */
  readonly selectOnMap: (value: EntitySelection) => void;
  /**
   * Set a **System Status** selection as the single bus focus (ADR-024 §4/§5
   * amended 2026-07-24; REQ-UI-051; decision 2026-07-24 "full bus integration").
   *
   * Sets ``inspected`` to the status value AND clears ``mapSelected``. A status
   * node has no map glyph, so it can only be the single focus by *removing* a
   * prior map ring — "a status selection clears a prior map/entity selection,
   * and vice versa." The reverse (a map/entity selection clearing a status one)
   * needs no special action: :func:`inspect` / :func:`selectOnMap` overwrite the
   * ``inspected`` slot the status value lived in.
   *
   * Distinct from :func:`inspect` (which leaves ``mapSelected`` untouched — the
   * list-browsing divergence) precisely because a status selection is a *single*
   * focus, not a reading-only focus that lets the map ring stay put.
   */
  readonly inspectStatus: (value: StatusSelection) => void;
  /**
   * Navigate from one selection to another **through a link inside
   * Selection Details**, recording where the operator came from
   * (REQ-UI-186).
   *
   * Behaves as :func:`inspect` — the ``inspected`` slot only, so the map
   * ring stays put — and additionally pushes the outgoing selection onto
   * :attr:`navigationHistory`. Nothing is pushed when there was no prior
   * selection, or when the link targets the entity already inspected (a
   * re-click is not a traversal, and pushing would put a selection into
   * its own history).
   *
   * Takes any :type:`SelectionValue`, not just an entity: a Query-reached
   * track IS a ``query-record`` selection, and stepping back to it is the
   * case the requirement was written for.
   */
  readonly inspectLinked: (value: SelectionValue) => void;
  /**
   * Step back to the selection the current one was navigated from, if
   * any; otherwise a no-op (REQ-UI-186).
   *
   * Pops :attr:`navigationHistory` into ``inspected``. ``mapSelected`` is
   * left untouched, for the same reason :func:`inspect` leaves it: the
   * forward step did not move the map ring, so the back step must not
   * either — the operator's geographic focus is not part of this
   * traversal.
   */
  readonly back: () => void;
  /** Clear BOTH slots (the single "nothing is focused" gesture). */
  readonly clear: () => void;
}

/**
 * Drop any selected map annotation — REQ-UI-105's "selecting an entity clears
 * the annotation selection" half.
 *
 * # Why the bus reaches across to another store
 *
 * A selected annotation renders in Entity Details with **unconditional
 * precedence** over the inspected entity (``entity-details-panel``), because
 * ADR-034 put per-shape configuration in that panel and the operator focuses
 * one thing at a time. That precedence is only coherent while the two
 * selections are mutually exclusive, and the annotation selection lives in its
 * own store rather than in this bus — so *something* has to clear it when the
 * bus moves. Until 2026-08-31 nothing did: the only clearing paths were a
 * background map click, Escape, and post-delete, so a list-row / Query-row /
 * System-Status click left the shape selected and the panel pinned to it,
 * unreachable without first deselecting the shape on the map.
 *
 * The rule lives **inside the write actions**, not in a subscriber, for two
 * reasons. It is synchronous with the selection change, so there is no frame in
 * which both are set and the panel flashes the shape. And it sits beside the
 * single-focus rule :func:`inspectStatus` already enforces by clearing
 * ``mapSelected`` — the same "one focus" contract, applied to the one selection
 * that lives outside this module.
 *
 * Hoisting annotations into this bus as a fifth ``SelectionKind`` would make
 * the exclusion structural rather than enforced, and is the better end state;
 * it is deliberately not attempted here, because it would touch the map layers,
 * palette, keyboard, drag and handle modules for what is otherwise a
 * four-line defect fix.
 *
 * Unconditional and idempotent: a partial ``set`` leaves ``activeTool`` and
 * ``drawStyle`` untouched, so clearing when nothing is selected is a no-op.
 *
 * Cross-references:
 *   - REQ-UI-105 (annotation select → Entity Details; mutual exclusion).
 *   - ADR-034 (config-in-Entity-Details, 2026-06-11) / ADR-024 §5 (one focus).
 */
function clearAnnotationSelection(): void {
  useAnnotationToolStore.getState().setSelectedId(null);
}

/**
 * The module-level Zustand store.
 *
 * Components consume this via selector functions (see the
 * ``useInspected`` / ``useMapSelected`` / ``useIsInspected`` / etc.
 * helpers below) to subscribe narrowly. Calling the store as a hook with
 * no selector returns the full state object — each consumer re-renders on
 * any state change — and is discouraged for narrowly-scoped consumers.
 *
 * For non-React write sites (e.g., MapLibre click handlers that fire
 * outside React's render tree), use
 * ``useSelectionStore.getState().selectOnMap(...)``.
 */

export const useSelectionStore = create<SelectionState>((set, get) => ({
  inspected: null,
  mapSelected: null,
  navigationHistory: [],
  inspect: (value) => {
    clearAnnotationSelection();
    // A new navigation root: the operator picked this out of a list, a map or
    // a Query result rather than arriving through a link, so there is nothing
    // behind it to step back to (see ``navigationHistory``).
    set({ inspected: value, navigationHistory: [] });
  },
  toggleInspect: (value) => {
    clearAnnotationSelection();
    if (isSameEntity(get().inspected, value)) {
      // The re-click is the list's deselect gesture: the same "nothing is
      // focused" reset as a background click (ADR-024 §5 locked default 2).
      set({ inspected: null, mapSelected: null, navigationHistory: [] });
      return;
    }
    set({ inspected: value, navigationHistory: [] });
  },
  inspectLinked: (value) => {
    clearAnnotationSelection();
    const current = get().inspected;
    // Nothing to come back from, or the link points at where we already are:
    // either way this is not a traversal, and pushing would put the current
    // selection into its own history.
    if (current === null || isSameSelection(current, value)) {
      set({ inspected: value });
      return;
    }
    const pushed = [...get().navigationHistory, current];
    set({
      inspected: value,
      // Drop from the front once bounded, so the steps nearest the operator
      // stay walkable and only the distant origin is forgotten.
      navigationHistory:
        pushed.length > NAVIGATION_HISTORY_LIMIT
          ? pushed.slice(pushed.length - NAVIGATION_HISTORY_LIMIT)
          : pushed,
    });
  },
  back: () => {
    const history = get().navigationHistory;
    if (history.length === 0) return;
    // Same annotation rule as every other bus write: a selected shape renders
    // with unconditional precedence in Selection Details (REQ-UI-105), so
    // leaving it set would land the operator on the shape rather than on the
    // selection they asked to return to.
    clearAnnotationSelection();
    set({
      inspected: history[history.length - 1],
      navigationHistory: history.slice(0, -1),
      // ``mapSelected`` is untouched: the forward link step did not move the
      // map ring, so the back step must not either.
    });
  },
  selectOnMap: (value) => {
    clearAnnotationSelection();
    set({ inspected: value, mapSelected: value, navigationHistory: [] });
  },
  inspectStatus: (value) => {
    clearAnnotationSelection();
    // Single focus: the status value becomes the inspected entity AND any prior
    // map ring is cleared (a status node has no map presence — ADR-024 §5
    // amended). One set() so subscribers see the two-slot change atomically.
    set({ inspected: value, mapSelected: null, navigationHistory: [] });
  },
  clear: () => {
    // The single "nothing is focused" gesture (background click / Escape) drops
    // the annotation too — otherwise a deselect would leave Entity Details
    // still rendering the shape it was pinned to.
    clearAnnotationSelection();
    set({ inspected: null, mapSelected: null, navigationHistory: [] });
  },
}));

// ---- id addressing ------------------------------------------------

/**
 * The ``id`` a selection is addressed by, or ``null`` for a kind that is not
 * addressed by one.
 *
 * ``status-interface`` is that kind: an interface is addressed by
 * ``(parentNodeId, interfaceName)``, so there is no id to return. The test is
 * **structural** (``"id" in value``) rather than a ``kind`` comparison so the
 * next id-less kind added to the union is handled the day it is added — a
 * ``kind``-based list would compile fine and start returning ``undefined`` at
 * runtime, which is exactly the silent-empty-panel failure REQ-STATUS-039's
 * Notes warn about.
 */
function selectionId(value: SelectionValue | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return "id" in value ? value.id : null;
}

/**
 * Whether ``current`` already holds ``candidate`` — same kind AND same id.
 * The kind matters: a detection and a track can share an id string in
 * principle, and a status selection carries no ``id`` at all (never equal).
 */
function isSameEntity(current: SelectionValue | null, candidate: EntitySelection): boolean {
  return (
    current !== null && current.kind === candidate.kind && selectionId(current) === candidate.id
  );
}

/**
 * Whether two selections address the same thing.
 *
 * Broader than :func:`isSameEntity`, which takes an
 * :type:`EntitySelection` and so cannot compare the id-less status kinds
 * or distinguish two ``query-record`` selections of different types. Used
 * by :func:`inspectLinked` to recognise a link back to where we already
 * are, which must not push a history entry.
 *
 * Compares the discriminant, the id (or its absence) and the extra
 * coordinate each composite kind carries — ``recordType`` for a query
 * record, ``(parentNodeId, interfaceName)`` for an interface,
 * ``(parentNodeId, peerName)`` for a peer, ``componentId`` for a
 * connection — because those are precisely the kinds not addressed by an
 * id alone.
 */
function isSameSelection(a: SelectionValue, b: SelectionValue): boolean {
  if (a.kind !== b.kind) return false;
  if (selectionId(a) !== selectionId(b)) return false;
  if (a.kind === "query-record" && b.kind === "query-record") {
    return a.recordType === b.recordType;
  }
  if (a.kind === "status-interface" && b.kind === "status-interface") {
    return a.parentNodeId === b.parentNodeId && a.interfaceName === b.interfaceName;
  }
  if (a.kind === "status-peer" && b.kind === "status-peer") {
    return a.parentNodeId === b.parentNodeId && a.peerName === b.peerName;
  }
  if (a.kind === "status-connection" && b.kind === "status-connection") {
    return a.componentId === b.componentId;
  }
  return true;
}

// ---- inspected-slot narrow-subscription helpers -------------------

/**
 * Subscribe to the current ``inspected`` value (or ``null``).
 *
 * Re-renders the calling component whenever any field of the inspected
 * selection changes. Drives the Entity Details panel. Prefer the more
 * specific hooks below (:func:`useInspectedIdForKind`,
 * :func:`useIsInspected`) when the consumer only cares about a slice.
 */
export function useInspected(): SelectionValue | null {
  return useSelectionStore((state) => state.inspected);
}

/**
 * Return the ``inspected`` entity's ID iff it's of the given kind;
 * ``null`` otherwise.
 *
 * The list panels use this for the kind they render (e.g. the Platforms
 * panel passes ``useInspectedIdForKind("platform")`` to the
 * keep-in-view hook) — they highlight + scroll the row matching the
 * *reading* focus, independent of the map ring. Subscribers only
 * re-render when the kind-matching ID changes.
 *
 * Returns ``null`` for the id-less ``status-interface`` kind (see
 * :func:`selectionId`); use :func:`useIsInspectedInterface` for that one.
 */
export function useInspectedIdForKind(kind: SelectionKind): string | null {
  return useSelectionStore((state) =>
    state.inspected?.kind === kind ? selectionId(state.inspected) : null,
  );
}

/**
 * Convenience: is this specific ``(kind, id)`` the current ``inspected``
 * selection?
 *
 * The list rows use this for their "highlight or not" boolean —
 * subscribers re-render on at most two transitions per selection change
 * (false → true → false).
 *
 * Always ``false`` for the id-less ``status-interface`` kind — see
 * :func:`useIsInspectedInterface`.
 */
export function useIsInspected(kind: SelectionKind, id: string): boolean {
  return useSelectionStore(
    (state) => state.inspected?.kind === kind && selectionId(state.inspected) === id,
  );
}

/**
 * Is this specific ``(parentNodeId, interfaceName)`` interface the current
 * ``inspected`` selection (REQ-STATUS-039)?
 *
 * The interface-icon equivalent of :func:`useIsInspected` — an icon's "ring or
 * not" boolean. Both coordinates are compared because interface names are
 * unique only within their parent, so a name alone would ring an icon on the
 * wrong node.
 */
export function useIsInspectedInterface(parentNodeId: string, interfaceName: string): boolean {
  return useSelectionStore(
    (state) =>
      state.inspected?.kind === "status-interface" &&
      state.inspected.parentNodeId === parentNodeId &&
      state.inspected.interfaceName === interfaceName,
  );
}

/**
 * The topology node the current status selection belongs to, or ``null`` when
 * the inspected value is not a status selection.
 *
 * All three levels resolve to a node so the **parent stays highlighted while a
 * child is inspected** (feature-spec §2.2 "Selection"): a component is its own
 * node, an interface reports its ``parentNodeId``, and a connection reports the
 * ``nodeId`` it was drilled from. Without this the diagram would drop its
 * highlight the moment an operator clicked an interface icon, making the
 * selected icon look unparented.
 */
export function useInspectedStatusNodeId(): string | null {
  return useSelectionStore((state) => {
    const selected = state.inspected;
    if (selected === null) return null;
    switch (selected.kind) {
      case "status-component":
        return selected.id;
      case "status-interface":
        return selected.parentNodeId;
      case "status-connection":
        return selected.nodeId;
      default:
        return null;
    }
  });
}

// ---- mapSelected-slot narrow-subscription helpers -----------------

/**
 * Subscribe to the current ``mapSelected`` value (or ``null``).
 *
 * Re-renders the calling component whenever any field of the map
 * selection changes. Prefer the more specific hooks below
 * (:func:`useMapSelectedIdForKind`, :func:`useIsMapSelected`) when the
 * consumer only cares about a slice.
 */
export function useMapSelected(): SelectionValue | null {
  return useSelectionStore((state) => state.mapSelected);
}

/**
 * Return the ``mapSelected`` entity's ID iff it's of the given kind;
 * ``null`` otherwise.
 *
 * The map layers use this for the kind they render (the snapshot layer's
 * platform ring, the detection/alert glyph rings + camera pan) so the
 * ring/pan track the *geographic* focus, independent of what the lists
 * are inspecting. Subscribers only re-render when the kind-matching ID
 * changes.
 */
export function useMapSelectedIdForKind(kind: SelectionKind): string | null {
  return useSelectionStore((state) =>
    state.mapSelected?.kind === kind ? selectionId(state.mapSelected) : null,
  );
}

/**
 * Convenience: is this specific ``(kind, id)`` the current
 * ``mapSelected`` selection?
 *
 * For map markers / glyphs that just need a boolean "ring or not".
 * Subscribers re-render on at most two transitions per selection change.
 */
export function useIsMapSelected(kind: SelectionKind, id: string): boolean {
  return useSelectionStore(
    (state) => state.mapSelected?.kind === kind && selectionId(state.mapSelected) === id,
  );
}

// ---- actions ------------------------------------------------------

/**
 * The store's write actions, as a stable-reference object.
 *
 * The three action functions are themselves stable across renders
 * (Zustand contract), so destructuring is safe::
 *
 *     const { inspect, selectOnMap, clear } = useSelectionActions();
 *
 * For non-React contexts (event handlers fired outside React's tree,
 * imperative orchestration), prefer
 * ``useSelectionStore.getState().selectOnMap(...)`` to avoid the
 * unnecessary subscription.
 */
export function useSelectionActions(): {
  inspect: SelectionState["inspect"];
  toggleInspect: SelectionState["toggleInspect"];
  inspectLinked: SelectionState["inspectLinked"];
  back: SelectionState["back"];
  selectOnMap: SelectionState["selectOnMap"];
  inspectStatus: SelectionState["inspectStatus"];
  clear: SelectionState["clear"];
} {
  // ``useShallow`` opts the selector into shallow-equality so the
  // returned ``{ inspect, selectOnMap, inspectStatus, clear }`` object doesn't
  // trigger a new subscription on every render. Without it Zustand v5's
  // reference-equality default would observe a fresh object each render
  // and re-render the consumer, producing an infinite loop.
  return useSelectionStore(
    useShallow((state) => ({
      inspect: state.inspect,
      toggleInspect: state.toggleInspect,
      inspectLinked: state.inspectLinked,
      back: state.back,
      selectOnMap: state.selectOnMap,
      inspectStatus: state.inspectStatus,
      clear: state.clear,
    })),
  );
}

/**
 * Subscribe to the selection the back control would return to, or
 * ``null`` when there is nothing to go back to (REQ-UI-186).
 *
 * The top of :attr:`navigationHistory`. Narrow by design: the control
 * needs the target (to name it) and whether there is one (to render at
 * all), not the whole stack.
 */
export function useNavigationBackTarget(): SelectionValue | null {
  return useSelectionStore((state) =>
    state.navigationHistory.length === 0
      ? null
      : state.navigationHistory[state.navigationHistory.length - 1],
  );
}

// ---- trail compatibility shim -------------------------------------

/**
 * The selected Producer ID for the **map** slot (or ``null`` when the
 * map selection isn't a platform).
 *
 * The platform trail (REQ-UI-012) is a map affordance, so it follows
 * ``mapSelected`` per the redesign's locked default — a plain list-row
 * left-click (which sets ``inspected`` only) must NOT redraw the trail.
 * Renamed from the pre-redesign ``useSelectedProducerId`` to make the
 * slot it reads explicit.
 *
 * Non-platform map selections return ``null`` here (e.g., a detection on
 * the map yields ``null`` — no platform trail to draw). Kept for the
 * TrailLayer's usage; new code should prefer
 * :func:`useMapSelectedIdForKind` directly.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail follows the map selection).
 *   - ADR-024 §5.
 */
export function useMapSelectedProducerId(): string | null {
  return useMapSelectedIdForKind("platform");
}
